import React, { useMemo, useState } from 'react';

// Drillable time matrix for OP_01: metrics down the rows, time across the
// columns. Default = fiscal years; click a year to expand to quarters, click
// a quarter to expand to months. Aggregation is measure-aware:
//   balance (EOP)  -> last month of span
//   average        -> mean of monthly averages
//   flow           -> sum across span
//   rate           -> recomputed from the span's flows/balances

const METRIC_ROWS = [
  { key: 'endingLoans', label: 'Ending loans', kind: 'eop', digits: 0 },
  { key: 'averageLoans', label: 'Average loans', kind: 'avg', digits: 0 },
  { key: 'endingDeposits', label: 'Ending deposits', kind: 'eop', digits: 0 },
  { key: 'averageDeposits', label: 'Average deposits', kind: 'avg', digits: 0 },
  { key: 'loanYield', label: 'Loan yield', kind: 'rate', digits: 2, numerator: 'interestIncome', denominator: 'averageLoans' },
  { key: 'depositCost', label: 'Deposit cost', kind: 'rate', digits: 2, numerator: 'interestExpense', denominator: 'averageDeposits' },
  { key: 'nii', label: 'NII', kind: 'flow', digits: 1 },
  { key: 'nonInterestIncome', label: 'Noninterest income', kind: 'flow', digits: 1 },
  { key: 'nonInterestExpense', label: 'Noninterest expense', kind: 'flow', digits: 1 },
  { key: 'creditProvision', label: 'Provision', kind: 'flow', digits: 1 },
  { key: 'taxExpense', label: 'Tax expense', kind: 'flow', digits: 1 },
  { key: 'netIncome', label: 'Net income', kind: 'flow', digits: 1 },
  { key: 'nim', label: 'NIM', kind: 'nim', digits: 2 },
  { key: 'efficiencyRatio', label: 'Efficiency ratio', kind: 'efficiency', digits: 1 }
];

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Actual/365 day count for rate annualization across dynamic spans.
const daysInMonth = (year, monthIndex) => new Date(year, monthIndex + 1, 0).getDate();
const rowDays = (row) => daysInMonth(row.fiscalYearNumber, (row.monthNumber - 1) % 12);

const fmtMM = (value, digits) =>
  `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}MM`;

const aggregate = (rows, metric) => {
  if (!rows.length) return null;
  const last = rows[rows.length - 1];
  if (metric.kind === 'eop') return last[metric.key];
  if (metric.kind === 'avg') return rows.reduce((s, r) => s + r[metric.key], 0) / rows.length;
  if (metric.kind === 'flow') return rows.reduce((s, r) => s + r[metric.key], 0);
  if (metric.kind === 'rate') {
    // Day-count aware: annualized rate = Σ flow / Σ (avg balance × days) × 365.
    const flow = rows.reduce((s, r) => s + r[metric.numerator], 0);
    const balanceDays = rows.reduce((s, r) => s + r[metric.denominator] * rowDays(r), 0);
    return balanceDays > 0 ? (flow / balanceDays) * 365 * 100 : 0;
  }
  if (metric.kind === 'nim') {
    const nii = rows.reduce((s, r) => s + r.nii, 0);
    const balanceDays = rows.reduce((s, r) => s + r.averageLoans * rowDays(r), 0);
    return balanceDays > 0 ? (nii / balanceDays) * 365 * 100 : 0;
  }
  if (metric.kind === 'efficiency') {
    const nii = rows.reduce((s, r) => s + r.nii, 0);
    const fees = rows.reduce((s, r) => s + r.nonInterestIncome, 0);
    const nie = rows.reduce((s, r) => s + r.nonInterestExpense, 0);
    return nii + fees > 0 ? (nie / (nii + fees)) * 100 : 0;
  }
  return null;
};

const formatCell = (value, metric) => {
  if (value === null || value === undefined) return '—';
  if (metric.kind === 'nim' || metric.kind === 'efficiency' || metric.kind === 'rate') return `${value.toFixed(metric.digits)}%`;
  return fmtMM(value, metric.digits);
};

const yearLabelFor = (year, rows) => {
  const sourceTypes = new Set(rows.map((row) => row.sourceType || 'forecast'));
  if (sourceTypes.size === 1 && sourceTypes.has('actual')) return `FY${year}A`;
  if (sourceTypes.has('actual') && sourceTypes.has('currentYearForecast')) return `FY${year} A+F`;
  if (sourceTypes.size === 1 && sourceTypes.has('forecast')) return `FY${year}F`;
  return `FY${year}`;
};

const columnClass = (column) =>
  [
    'time-col',
    column.kind.toLowerCase(),
    column.sourceType || '',
    column.clickable ? 'clickable' : ''
  ].filter(Boolean).join(' ');

const ForecastTimeMatrix = ({ monthlySeries }) => {
  const [expanded, setExpanded] = useState({});

  const years = useMemo(
    () => [...new Set(monthlySeries.map((row) => row.fiscalYearNumber))],
    [monthlySeries]
  );

  // Column model: [{id, label, kind: 'Y'|'Q'|'M', year, quarter?, rows}]
  const columns = useMemo(() => {
    const cols = [];
    years.forEach((year) => {
      const yearRows = monthlySeries.filter((row) => row.fiscalYearNumber === year);
      const actualRows = yearRows.filter((row) => row.sourceType === 'actual');
      const currentYearForecastRows = yearRows.filter((row) => row.sourceType === 'currentYearForecast');
      if (!expanded[`Y${year}`]) {
        if (actualRows.length && currentYearForecastRows.length) {
          cols.push({
            id: `AY${year}`,
            label: `${year} YTD A`,
            kind: 'YA',
            year,
            rows: actualRows,
            sourceType: 'actual',
            clickable: false
          });
          cols.push({
            id: `RY${year}`,
            label: `${year} Rem F`,
            kind: 'YR',
            year,
            rows: currentYearForecastRows,
            sourceType: 'currentYearForecast',
            clickable: false
          });
          cols.push({
            id: `YT${year}`,
            label: `FY${year} Σ`,
            kind: 'YT',
            year,
            rows: yearRows,
            sourceType: 'mixed',
            clickable: true
          });
          return;
        }
        cols.push({
          id: `Y${year}`,
          label: yearLabelFor(year, yearRows),
          kind: 'Y',
          year,
          rows: yearRows,
          sourceType: yearRows[0]?.sourceType || 'forecast',
          clickable: true
        });
        return;
      }
      [1, 2, 3, 4].forEach((quarter) => {
        const quarterRows = yearRows.filter(
          (row) => Math.floor((row.monthNumber - 1) % 12 / 3) + 1 === quarter
        );
        if (!expanded[`Q${year}-${quarter}`]) {
          cols.push({
            id: `Q${year}-${quarter}`, label: `${String(year).slice(2)}Q${quarter}`,
            kind: 'Q', year, quarter, rows: quarterRows,
            sourceType: quarterRows.some((row) => row.sourceType === 'actual') ? 'actual' : quarterRows[0]?.sourceType || 'forecast',
            clickable: true
          });
          return;
        }
        quarterRows.forEach((row) => {
          const monthIndex = (row.monthNumber - 1) % 12;
          cols.push({
            id: `M${year}-${monthIndex}`, label: `${MONTH_SHORT[monthIndex]}-${String(year).slice(2)}`,
            kind: 'M', year, quarter, rows: [row],
            sourceType: row.sourceType || 'forecast',
            clickable: true
          });
        });
      });
      cols.push({
        id: `YT${year}`,
        label: `FY${year} Σ`,
        kind: 'YT',
        year,
        rows: yearRows,
        sourceType: yearRows.some((row) => row.sourceType === 'actual') ? 'mixed' : yearRows[0]?.sourceType || 'forecast',
        clickable: true
      });
    });
    return cols;
  }, [years, monthlySeries, expanded]);

  const toggle = (column) => {
    if (column.kind === 'Y') {
      setExpanded((previous) => ({ ...previous, [`Y${column.year}`]: true }));
    } else if (column.kind === 'YT') {
      setExpanded((previous) => {
        const next = { ...previous };
        delete next[`Y${column.year}`];
        [1, 2, 3, 4].forEach((q) => delete next[`Q${column.year}-${q}`]);
        return next;
      });
    } else if (column.kind === 'Q') {
      setExpanded((previous) => ({ ...previous, [`Q${column.year}-${column.quarter}`]: true }));
    } else if (column.kind === 'M') {
      setExpanded((previous) => {
        const next = { ...previous };
        delete next[`Q${column.year}-${column.quarter}`];
        return next;
      });
    }
  };

  return (
    <div className="forecast-table-shell ace-scroll-shell forecast-compact-table">
      <table className="forecast-table forecast-time-matrix">
        <thead>
          <tr>
            <th>Metric</th>
            {columns.map((column) => (
              <th
                key={column.id}
                className={columnClass(column)}
                title={
                  !column.clickable ? 'Current-year source split'
                    : column.kind === 'Y' ? 'Click to expand to quarters'
                    : column.kind === 'Q' ? 'Click to expand to months'
                    : column.kind === 'M' ? 'Click to collapse to quarter'
                    : 'Click to collapse year'
                }
                onClick={() => column.clickable && toggle(column)}
              >
                {column.label}
                {column.clickable && (
                  <span className="time-col-affordance">
                    {column.kind === 'Y' ? ' ▸' : column.kind === 'Q' ? ' ▸' : column.kind === 'M' ? ' ▾' : ' ✕'}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {METRIC_ROWS.map((metric) => (
            <tr key={metric.key}>
              <td><strong>{metric.label}</strong>
                <small className="forecast-home-market">
                  {metric.kind === 'eop' ? ' EOP' : metric.kind === 'avg' ? ' avg' : metric.kind === 'flow' ? ' Σ' : metric.kind === 'rate' ? ' act/365' : ''}
                </small>
              </td>
              {columns.map((column) => (
                <td key={`${metric.key}-${column.id}`} className={`time-cell ${column.kind.toLowerCase()}`}>
                  {formatCell(aggregate(column.rows, metric), metric)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="forecast-submit-note">
        Time runs across the columns. Click a fiscal year to drill to quarters, a quarter to drill to
        months; the FY Σ column collapses the year. When the current year has both actuals and forecast,
        the matrix separates actual YTD from remaining forecast and then shows the full-year sum.
        Balances show end-of-period, averages are means of monthly averages, P&L lines sum, and rates
        (loan yield, deposit cost, NIM) are recomputed for the span on an actual/365 day-count basis —
        never averaged across columns.
      </p>
    </div>
  );
};

export default ForecastTimeMatrix;
